import { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { useFreelancerProfile } from '@/lib/hooks/use-freelancer-profile';
import { useFreelancerSkills } from '@/lib/hooks/use-freelancer-skills';
import { useRole } from '@/hooks/use-role';
import { PortfolioSection } from '@/components/portfolio/portfolio-section';
import { useProfileCompletion } from '@/lib/hooks/use-profile-completion';
import { useUnifiedCourses } from '@/lib/hooks/use-unified-courses';
import { usePortfolioProjects } from '@/lib/hooks/use-portfolio-projects';
import { useWorkExperience } from '@/lib/hooks/use-work-experience';
import { WorkExperienceSection } from '@/components/profile/work-experience-section';
import { emailService } from '@/lib/api/services/email.service';
import { VideoIntroPlayer } from '@/components/profile/video-intro-player';
import { getVideoEmbedInfo } from '@/lib/utils/video';
import { FadeIn } from '@/components/motion';
import { useAIWritingAssistant } from '@/lib/hooks/use-ai-writing-assistant';
import { ProfileHeader } from '@/components/profile/genius-profile/profile-header';
import { ProfileCompletionCard } from '@/components/profile/genius-profile/profile-completion-card';
import { AvailabilityCard } from '@/components/profile/genius-profile/availability-card';
import { RateRangeCard } from '@/components/profile/genius-profile/rate-range-card';
import { ProfessionalDetailsCard } from '@/components/profile/genius-profile/professional-details-card';
import { LanguagesCard } from '@/components/profile/genius-profile/languages-card';
import { VideoIntroductionCard } from '@/components/profile/genius-profile/video-introduction-card';
import { BioSection } from '@/components/profile/genius-profile/bio-section';
import { SkillsSection } from '@/components/profile/genius-profile/skills-section';
import { TrainingProgressSection } from '@/components/profile/genius-profile/training-progress-section';
import { SearchableToggle } from '@/components/profile/genius-profile/searchable-toggle';
import { SubmitForReviewSection } from '@/components/profile/genius-profile/submit-for-review-section';
import { ChangesRequestedBanner } from '@/components/profile/genius-profile/changes-requested-banner';
import { IncompleteProfileBanner } from '@/components/profile/genius-profile/incomplete-profile-banner';
import { CertificationProfileBanner } from '@/components/certification/CertificationProfileBanner';

export default function GeniusProfile() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { freelancerProfileId } = useRole();
  const {
    freelancerProfile,
    saveFreelancerProfile,
    // isSaving,
    reviewStatus,
    reviewComments,
    submitForReview,
    isSubmittingForReview
  } = useFreelancerProfile(freelancerProfileId, { enableMutations: true });
  const { data: suggestedSkills = [], isLoading: isLoadingSkills } = useFreelancerSkills();

  // Get course progress for completion tracking
  const { courses } = useUnifiedCourses();
  const { projects: portfolioProjects } = usePortfolioProjects(freelancerProfileId || '');
  const { experiences: workExperiences } = useWorkExperience(freelancerProfileId || '');
  const { generateHeadline, generateBio, isLoading: isAIGenerating, generatingType, data: aiGeneratedData, reset: resetAIData } = useAIWritingAssistant();

  // Calculate course progress
  const enrolledCourses = courses?.filter((c) => c.enrollment) || [];
  const completedCourses = enrolledCourses.filter(
    (c) => c.enrollment?.status === "completed"
  ).length;
  const totalCourses = courses?.length || 0;

  const courseProgress = {
    totalCourses,
    completedCourses,
    allCompleted: completedCourses >= totalCourses && totalCourses > 0,
  };

  // Calculate profile completion
  const profileCompletion = useProfileCompletion(
    freelancerProfile,
    courseProgress,
    portfolioProjects?.length || 0,
    workExperiences?.length || 0
  );

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [location, setLocation] = useState('');
  const [isSearchable, setIsSearchable] = useState(true);
  const [skills, setSkills] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [availabilityStatus, setAvailabilityStatus] = useState<string>('');
  const [rateRangeMin, setRateRangeMin] = useState<string>('');
  const [rateRangeMax, setRateRangeMax] = useState<string>('');
  const [videoIntroUrls, setVideoIntroUrls] = useState<string[]>([]);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  // Editing states for sections
  const [isEditingNameHeadline, setIsEditingNameHeadline] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState('');
  const [tempHeadline, setTempHeadline] = useState('');
  const [tempLocation, setTempLocation] = useState('');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [tempBio, setTempBio] = useState('');
  const [isEditingSkills, setIsEditingSkills] = useState(false);
  const [isEditingLanguages, setIsEditingLanguages] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isEditingAvailability, setIsEditingAvailability] = useState(false);
  const [isEditingRateRange, setIsEditingRateRange] = useState(false);
  const [isEditingVideoIntros, setIsEditingVideoIntros] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [isAIHeadlineDialogOpen, setIsAIHeadlineDialogOpen] = useState(false);

  // Loading states for save operations
  const [isSavingNameHeadline, setIsSavingNameHeadline] = useState(false);
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [isSavingSkills, setIsSavingSkills] = useState(false);
  const [isSavingLanguages, setIsSavingLanguages] = useState(false);
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);
  const [isSavingRateRange, setIsSavingRateRange] = useState(false);
  const [isSavingVideoIntros, setIsSavingVideoIntros] = useState(false);

  // Rate range validation errors
  const [rateRangeErrors, setRateRangeErrors] = useState<{
    min?: string;
    max?: string;
  }>({});

  // Track last processed AI data to avoid re-processing
  const lastProcessedAIDataRef = useRef<string | null>(null);

  // Sync local state with freelancer profile data
  useEffect(() => {
    if (freelancerProfile) {
      setDisplayName(freelancerProfile.display_name || '');
      setHeadline(freelancerProfile.headline || '');
      setBio(freelancerProfile.bio || '');
      setLinkedinUrl(freelancerProfile.linkedin_url || '');
      setWebsiteUrl(freelancerProfile.website_url || '');
      setLocation(freelancerProfile.location || '');
      setIsSearchable(freelancerProfile.is_searchable ?? true);
      setSkills(Array.isArray(freelancerProfile.skills) ? (freelancerProfile.skills as string[]) : []);
      setLanguages(Array.isArray(freelancerProfile.languages) ? (freelancerProfile.languages as string[]) : []);
      
      // New fields with type casting
      const profileWithNewFields = freelancerProfile as typeof freelancerProfile & {
        availability_status?: string | null;
        rate_range_min_cents?: number | null;
        rate_range_max_cents?: number | null;
        video_intro_urls?: string[] | null;
        view_count?: number;
      };
      
      setAvailabilityStatus(profileWithNewFields.availability_status || '');
      setRateRangeMin(profileWithNewFields.rate_range_min_cents ? (profileWithNewFields.rate_range_min_cents / 100).toString() : '');
      setRateRangeMax(profileWithNewFields.rate_range_max_cents ? (profileWithNewFields.rate_range_max_cents / 100).toString() : '');
      setVideoIntroUrls(Array.isArray(profileWithNewFields.video_intro_urls) ? profileWithNewFields.video_intro_urls : []);
    }
  }, [freelancerProfile]);

  // Auto-enter edit mode when AI generates text and update temp values
  useEffect(() => {
    if (aiGeneratedData) {
      // Create a unique key for this AI data to track if we've processed it
      const dataKey = `${aiGeneratedData.type}-${aiGeneratedData.text}`;
      
      // Skip if we've already processed this data
      if (lastProcessedAIDataRef.current === dataKey) {
        return;
      }

      if (aiGeneratedData.type === 'headline') {
        // Update tempHeadline with generated text
        if (aiGeneratedData.text) {
          setTempHeadline(aiGeneratedData.text);
        }
        // Auto-enter edit mode if not already editing
        setIsEditingNameHeadline((prev) => {
          if (!prev) {
            // Initialize tempDisplayName if not already set
            setTempDisplayName((prevName) => prevName || displayName);
            return true;
          }
          return prev;
        });
        lastProcessedAIDataRef.current = dataKey;
      } else if (aiGeneratedData.type === 'bio') {
        // Update tempBio with generated text
        if (aiGeneratedData.text) {
          setTempBio(aiGeneratedData.text);
        }
        // Auto-enter edit mode if not already editing
        setIsEditingBio((prev) => {
          if (!prev) {
            return true;
          }
          return prev;
        });
        lastProcessedAIDataRef.current = dataKey;
      }
    }
  }, [aiGeneratedData, displayName]);

  // Send email when profile reaches 100% completion (only once)
  useEffect(() => {
    if (!user?.id || !profileCompletion.isComplete || !freelancerProfile?.email) return;
    
    const storageKey = `genius-profile-completion-email-sent-${user.id}`;
    const hasSentEmail = localStorage.getItem(storageKey) === 'true';
    
    if (!hasSentEmail) {
      // Mark as sent immediately to prevent duplicate sends
      localStorage.setItem(storageKey, 'true');
      
      // Send completion email (non-blocking)
      emailService.sendGeniusProfileCompletedEmail(freelancerProfile.email).catch((error) => {
        console.error('Failed to send profile completion email:', error);
        // Remove the flag on error so we can retry
        localStorage.removeItem(storageKey);
      });
    }
  }, [profileCompletion.isComplete, freelancerProfile?.email, user?.id]);

  // Handle hash navigation scrolling
  useEffect(() => {
    const scrollToPortfolio = () => {
      if (window.location.hash === '#portfolio') {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById('portfolio');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    // Check on mount
    scrollToPortfolio();

    // Listen for hash changes
    window.addEventListener('hashchange', scrollToPortfolio);

    return () => {
      window.removeEventListener('hashchange', scrollToPortfolio);
    };
  }, []);

  const addSkill = (skill: string) => {
    if (!skills.includes(skill) && skills.length < 10) {
      // Always enter edit mode when adding skills so user can continue adding more
      // This ensures the editing interface stays open until user explicitly saves
      setIsEditingSkills(true);
      setSkills(prevSkills => [...prevSkills, skill]);
    }
  };

  const removeSkill = (skill: string) => {
    const newSkills = skills.filter(s => s !== skill);
    setSkills(newSkills);
  };

  // Save handlers
  const saveNameHeadline = async () => {
    if (!saveFreelancerProfile) return;

    setIsSavingNameHeadline(true);
    try {
      await saveFreelancerProfile({
        display_name: tempDisplayName.trim(),
        headline: tempHeadline.trim() || null,
        location: tempLocation.trim() || null,
      });
      setDisplayName(tempDisplayName.trim());
      setHeadline(tempHeadline.trim());
      setLocation(tempLocation.trim());
      setIsEditingNameHeadline(false);
      // Reset AI data and ref after saving to prevent re-entering edit mode
      if (aiGeneratedData?.type === 'headline') {
        resetAIData();
        lastProcessedAIDataRef.current = null;
      }
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingNameHeadline(false);
    }
  };

  const saveBio = async () => {
    if (!saveFreelancerProfile) return;

    setIsSavingBio(true);
    try {
      await saveFreelancerProfile({ bio: tempBio.trim() || null });
      setBio(tempBio.trim());
      setIsEditingBio(false);
      // Reset AI data and ref after saving to prevent re-entering edit mode
      if (aiGeneratedData?.type === 'bio') {
        resetAIData();
        lastProcessedAIDataRef.current = null;
      }
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingBio(false);
    }
  };

  const saveSkills = async () => {
    if (!saveFreelancerProfile) return;

    setIsSavingSkills(true);
    try {
      await saveFreelancerProfile({ skills: skills.length > 0 ? skills : null });
      setIsEditingSkills(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingSkills(false);
    }
  };

  const saveLanguages = async () => {
    if (!saveFreelancerProfile) return;

    setIsSavingLanguages(true);
    try {
      await saveFreelancerProfile({ languages: languages.length > 0 ? languages : null });
      setIsEditingLanguages(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingLanguages(false);
    }
  };

  const saveDetails = async () => {
    if (!saveFreelancerProfile) return;

    setIsSavingDetails(true);
    try {
      await saveFreelancerProfile({
        linkedin_url: linkedinUrl.trim() || null,
        website_url: websiteUrl.trim() || null,
      });
      setIsEditingDetails(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingDetails(false);
    }
  };

  const saveAvailability = async () => {
    if (!saveFreelancerProfile) return;

    setIsSavingAvailability(true);
    try {
      await saveFreelancerProfile({
        availability_status: availabilityStatus.trim() || null,
      } as Record<string, unknown>);
      setIsEditingAvailability(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingAvailability(false);
    }
  };

  const validateRateRange = (min: string, max: string): { min?: string; max?: string } => {
    const errors: { min?: string; max?: string } = {};
    
    const minValue = min.trim();
    const maxValue = max.trim();
    
    // Both fields are required
    if (!minValue) {
      errors.min = 'Minimum rate is required';
    }
    
    if (!maxValue) {
      errors.max = 'Maximum rate is required';
    }
    
    // Validate min
    if (minValue) {
      const minNum = parseFloat(minValue);
      if (isNaN(minNum)) {
        errors.min = 'Please enter a valid number';
      } else if (!Number.isInteger(minNum)) {
        errors.min = 'Rate must be a whole number';
      } else if (minNum < 1) {
        errors.min = 'Rate must be at least $1/hr';
      } else if (minNum > 10000) {
        errors.min = 'Rate cannot exceed $10,000/hr';
      }
    }
    
    // Validate max
    if (maxValue) {
      const maxNum = parseFloat(maxValue);
      if (isNaN(maxNum)) {
        errors.max = 'Please enter a valid number';
      } else if (!Number.isInteger(maxNum)) {
        errors.max = 'Rate must be a whole number';
      } else if (maxNum < 1) {
        errors.max = 'Rate must be at least $1/hr';
      } else if (maxNum > 10000) {
        errors.max = 'Rate cannot exceed $10,000/hr';
      }
    }
    
    // Validate min <= max if both are provided and valid
    if (minValue && maxValue && !errors.min && !errors.max) {
      const minNum = parseFloat(minValue);
      const maxNum = parseFloat(maxValue);
      if (minNum > maxNum) {
        errors.min = 'Minimum rate cannot be greater than maximum rate';
        errors.max = 'Maximum rate cannot be less than minimum rate';
      }
    }
    
    return errors;
  };

  const saveRateRange = async () => {
    // Validate before saving
    const errors = validateRateRange(rateRangeMin, rateRangeMax);
    setRateRangeErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the validation errors before saving');
      return;
    }
    
    // Both values are required and validated, so they should be present
    const minCents = Math.round(parseFloat(rateRangeMin.trim()) * 100);
    const maxCents = Math.round(parseFloat(rateRangeMax.trim()) * 100);
    
    // Additional safety check (should not be needed due to validation, but just in case)
    if (minCents < 100 || maxCents < 100) {
      toast.error('Rates must be at least $1/hr');
      return;
    }
    
    if (!saveFreelancerProfile) return;

    setIsSavingRateRange(true);
    try {
      await saveFreelancerProfile({
        rate_range_min_cents: minCents,
        rate_range_max_cents: maxCents,
      } as Record<string, unknown>);
      setIsEditingRateRange(false);
      setRateRangeErrors({});
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingRateRange(false);
    }
  };

  const saveVideoIntros = async () => {
    const trimmedUrl = newVideoUrl.trim();

    // If empty, clear the video intro
    if (!trimmedUrl) {
      if (!saveFreelancerProfile) return;

      setIsSavingVideoIntros(true);
      try {
        await saveFreelancerProfile({
          video_intro_urls: null,
        } as Record<string, unknown>);
        setVideoIntroUrls([]);
        setIsEditingVideoIntros(false);
      } catch (_error) {
        // Error is already handled by the mutation's onError handler
        // Don't exit edit mode on error
      } finally {
        setIsSavingVideoIntros(false);
      }
      return;
    }

    // Validate URL format (validation is also done in component, this is a safety check)
    const videoInfo = getVideoEmbedInfo(trimmedUrl);
    if (!videoInfo.type) {
      // Validation error is shown in the component, just return silently
      return;
    }

    if (!saveFreelancerProfile) return;

    setIsSavingVideoIntros(true);
    try {
      await saveFreelancerProfile({
        video_intro_urls: [trimmedUrl],
      } as Record<string, unknown>);
      // Only one intro link is allowed
      const nextUrls = [trimmedUrl];
      setVideoIntroUrls(nextUrls);
      setIsEditingVideoIntros(false);
    } catch (_error) {
      // Error is already handled by the mutation's onError handler
      // Don't exit edit mode on error
    } finally {
      setIsSavingVideoIntros(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        {/* Back Button */}
        <FadeIn>
          <Button
            variant="ghost"
            onClick={() => navigate('/genius/dashboard')}
            className="mb-6 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </FadeIn>

        {/* Certification Opt-In Banner */}
        <CertificationProfileBanner />

        {/* Changes Requested Banner */}
        {reviewStatus === 'changes_requested' && (
          <ChangesRequestedBanner reviewComments={reviewComments || undefined} />
        )}

        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left Sidebar */}
          <div className="w-full lg:w-[350px] flex-shrink-0 space-y-4 sm:space-y-6">

            {/* Profile Card */}
            <ProfileHeader
              freelancerProfileId={freelancerProfileId || ''}
              profilePictureUrl={freelancerProfile?.profile_picture_url || null}
              displayName={displayName}
              headline={headline}
              location={location}
              reviewStatus={reviewStatus}
              viewCount={(freelancerProfile as typeof freelancerProfile & { view_count?: number } | undefined)?.view_count}
              isEditingNameHeadline={isEditingNameHeadline}
              tempDisplayName={tempDisplayName}
              tempHeadline={tempHeadline}
              tempLocation={tempLocation}
              isSavingNameHeadline={isSavingNameHeadline}
              isAIGenerating={isAIGenerating}
              generatingType={generatingType}
              onEditClick={() => {
                setTempDisplayName(displayName);
                setTempHeadline(headline);
                setTempLocation(location);
                setIsEditingNameHeadline(true);
              }}
              onCancel={() => {
                setTempDisplayName(displayName);
                setTempHeadline(headline);
                setTempLocation(location);
                setIsEditingNameHeadline(false);
              }}
              onSave={saveNameHeadline}
              onDisplayNameChange={setTempDisplayName}
              onHeadlineChange={setTempHeadline}
              onLocationChange={setTempLocation}
              isAIHeadlineDialogOpen={isAIHeadlineDialogOpen}
              aiGeneratedHeadline={aiGeneratedData?.type === 'headline' ? aiGeneratedData.text : null}
              onAIHeadlineDialogOpenChange={setIsAIHeadlineDialogOpen}
              onGenerateHeadline={async (currentText, options) => {
                return await generateHeadline(currentText ?? tempHeadline, options);
              }}
              onAcceptHeadline={(text) => {
                setTempHeadline(text);
                resetAIData();
              }}
              onResetAIData={resetAIData}
            />

            {/* Profile Completion - Only show if not complete */}
            <ProfileCompletionCard
              percentage={profileCompletion.percentage}
              items={profileCompletion.items.map(item => ({
                ...item,
                description: item.description || ''
              }))}
            />

            {/* Availability Status Card */}
            <AvailabilityCard
              availabilityStatus={availabilityStatus}
              isEditing={isEditingAvailability}
              isSaving={isSavingAvailability}
              onEditClick={() => setIsEditingAvailability(true)}
              onCancel={() => setIsEditingAvailability(false)}
              onSave={saveAvailability}
              onStatusChange={setAvailabilityStatus}
            />

            {/* Rate Range Card */}
            <RateRangeCard
              rateRangeMin={rateRangeMin}
              rateRangeMax={rateRangeMax}
              isEditing={isEditingRateRange}
              isSaving={isSavingRateRange}
              errors={rateRangeErrors}
              onEditClick={() => {
                setIsEditingRateRange(true);
                setRateRangeErrors({});
              }}
              onCancel={() => {
                setIsEditingRateRange(false);
                setRateRangeErrors({});
              }}
              onSave={saveRateRange}
              onMinChange={(value) => {
                setRateRangeMin(value);
                const errors = validateRateRange(value, rateRangeMax);
                setRateRangeErrors(errors);
              }}
              onMaxChange={(value) => {
                setRateRangeMax(value);
                const errors = validateRateRange(rateRangeMin, value);
                setRateRangeErrors(errors);
              }}
              onValidate={validateRateRange}
            />

            {/* Professional Details Card - Sidebar */}
            <ProfessionalDetailsCard
              linkedinUrl={linkedinUrl}
              websiteUrl={websiteUrl}
              isEditing={isEditingDetails}
              isSaving={isSavingDetails}
              onEditClick={() => setIsEditingDetails(true)}
              onCancel={() => setIsEditingDetails(false)}
              onSave={saveDetails}
              onLinkedinUrlChange={setLinkedinUrl}
              onWebsiteUrlChange={setWebsiteUrl}
            />

            {/* Languages Card */}
            <LanguagesCard
              languages={languages}
              isEditing={isEditingLanguages}
              isSaving={isSavingLanguages}
              onEditClick={() => setIsEditingLanguages(true)}
              onCancel={() => setIsEditingLanguages(false)}
              onSave={saveLanguages}
              onLanguageToggle={(language) => {
                if (languages.includes(language)) {
                  setLanguages(languages.filter(l => l !== language));
                } else {
                  setLanguages([...languages, language]);
                }
              }}
              onLanguagesChange={setLanguages}
            />

            {/* Video Introduction Card */}
            <VideoIntroductionCard
              videoIntroUrls={videoIntroUrls}
              newVideoUrl={newVideoUrl}
              isEditing={isEditingVideoIntros}
              isSaving={isSavingVideoIntros}
              onEditClick={() => {
                setNewVideoUrl(videoIntroUrls[0] || '');
                setIsEditingVideoIntros(true);
              }}
              onCancel={() => {
                setNewVideoUrl(videoIntroUrls[0] || '');
                setIsEditingVideoIntros(false);
              }}
              onSave={saveVideoIntros}
              onVideoUrlChange={setNewVideoUrl}
              onVideoSelect={setSelectedVideoUrl}
            />
          </div>

          {/* Right Content */}
          <div className="flex-1 space-y-4 sm:space-y-6">

            {/* Incomplete Profile Banner */}
            <IncompleteProfileBanner percentage={profileCompletion.percentage} />

            {/* What I Do / Bio Section */}
            <BioSection
              bio={bio}
              tempBio={tempBio}
              isEditing={isEditingBio}
              isSaving={isSavingBio}
              isAIDialogOpen={isAIDialogOpen}
              isAIGenerating={isAIGenerating}
              generatingType={generatingType}
              aiGeneratedText={aiGeneratedData?.type === 'bio' ? aiGeneratedData.text : null}
              onEditClick={() => {
                setTempBio(bio);
                setIsEditingBio(true);
              }}
              onCancel={() => setIsEditingBio(false)}
              onSave={saveBio}
              onBioChange={setTempBio}
              onAIDialogOpenChange={setIsAIDialogOpen}
              onGenerate={async (currentText, options) => {
                return await generateBio(currentText ?? tempBio, options);
              }}
              onAccept={(text) => {
                setTempBio(text);
                resetAIData();
              }}
              onResetAIData={resetAIData}
            />

            {/* Skills & Expertise Section */}
            <SkillsSection
              skills={skills}
              suggestedSkills={suggestedSkills}
              isLoadingSkills={isLoadingSkills}
              isEditing={isEditingSkills}
              isSaving={isSavingSkills}
              onEditClick={() => setIsEditingSkills(true)}
              onCancel={() => {
                setSkills(Array.isArray(freelancerProfile?.skills) ? (freelancerProfile?.skills as string[]) : []);
                setIsEditingSkills(false);
              }}
              onSave={saveSkills}
              onAddSkill={addSkill}
              onRemoveSkill={removeSkill}
              onResetSkills={() => {
                setSkills(Array.isArray(freelancerProfile?.skills) ? (freelancerProfile?.skills as string[]) : []);
                setIsEditingSkills(false);
              }}
            />

            {/* Work Experience Section */}
            {freelancerProfileId && (
              <WorkExperienceSection
                freelancerProfileId={freelancerProfileId}
                isOwner={true}
              />
            )}

            {/* Portfolio Projects Section */}
            {freelancerProfileId && (
              <PortfolioSection
                freelancerProfileId={freelancerProfileId}
                isOwner={true}
              />
            )}

            {/* Training Progress Section */}
            <TrainingProgressSection courses={(courses || []).map(course => ({
              id: course.id,
              slug: course.slug,
              title: course.title,
              description: course.description || '',
              thumbnail_url: course.thumbnail_url,
              lessonsCount: course.lessonsCount,
              enrollment: course.enrollment ? {
                status: course.enrollment.status || 'in_progress'
              } : null
            }))} />

            {/* Make Profile Searchable */}
            {profileCompletion.percentage === 100 && (
              <SearchableToggle
                isSearchable={isSearchable}
                onToggle={(checked) => {
                  setIsSearchable(checked);
                  saveFreelancerProfile?.({ is_searchable: checked });
                }}
              />
            )}

            {/* Submit for Review Section */}
            {freelancerProfile && (
              <SubmitForReviewSection
                reviewStatus={reviewStatus}
                profileCompletionPercentage={profileCompletion.percentage}
                displayName={displayName}
                isSubmitting={isSubmittingForReview || false}
                onSubmit={() => submitForReview?.()}
              />
            )}

          </div>
        </div>
      </div>

      {/* Video Player Dialog */}
      {selectedVideoUrl && (
        <VideoIntroPlayer
          url={selectedVideoUrl}
          isOpen={!!selectedVideoUrl}
          onClose={() => setSelectedVideoUrl(null)}
        />
      )}
    </div>
  );
}
